'use client';

import { useEffect, useRef } from 'react';

interface ConfirmDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'default';
  loading?: boolean;
  /** A treia cale, opțională (ex. „Continuă oricum”): Anulează/ESC nu o declanșează, doar clicul pe ea. */
  secondaryText?: string;
  onSecondary?: () => void;
}

export function ConfirmDialog({
  open,
  onConfirm,
  onCancel,
  title,
  description,
  confirmText = 'Confirma',
  cancelText = 'Anuleaza',
  variant = 'default',
  loading = false,
  secondaryText,
  onSecondary,
}: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) {
      // Focus confirm button when dialog opens
      setTimeout(() => confirmRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [open, onCancel]);

  if (!open) return null;

  const isDanger = variant === 'danger';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative w-full max-w-sm mx-4 rounded-2xl bg-white dark:bg-neutral-900 shadow-2xl dark:ring-1 dark:ring-white/10 animate-in fade-in zoom-in-95 duration-200">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">{title}</h3>
          {description && (
            <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">{description}</p>
          )}
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-neutral-200 dark:border-neutral-800 px-6 py-4">
          <button
            onClick={onCancel}
            disabled={loading}
            className="rounded-lg border border-neutral-300 dark:border-neutral-600 px-4 py-2.5 text-sm font-medium text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors disabled:opacity-50"
          >
            {cancelText}
          </button>
          {secondaryText && onSecondary && (
            <button
              onClick={onSecondary}
              disabled={loading}
              className="rounded-lg border border-neutral-300 dark:border-neutral-600 px-4 py-2.5 text-sm font-medium text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors disabled:opacity-50"
            >
              {secondaryText}
            </button>
          )}
          <button
            ref={confirmRef}
            onClick={onConfirm}
            disabled={loading}
            className={
              'rounded-lg px-4 py-2.5 text-sm font-medium text-white transition-colors disabled:opacity-50 ' +
              (isDanger
                ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                : 'bg-primary hover:bg-primary-700 focus:ring-primary')
            }
          >
            {loading ? 'Se proceseaza...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
